import { createContext, useContext, type FC, type ReactNode } from 'react';

import type { DistributedOmit } from './types';

export interface FeatureFlag<K extends string, V extends string> {
  key: K;
  reason: string;
  variant: V | 'control';
}

interface FeatureFlagsContext<
  F extends FeatureFlag<K, V>,
  K extends string = F['key'],
  V extends string = F['variant'],
> {
  /**
   * useFeatureFlag is the hook that gets the feature flag for a given key:
   * - If the flag is found, return as-is
   * - If the flag is NOT found, fallback to a local fallback `control` variant
   */
  useFeatureFlag: <P extends F['key']>(key: P) => Extract<F, { key: P }>;
  FeatureFlagsProvider: FC<{
    /**
     * Feature flags data received from Hirer Graph
     */
    featureFlags: Array<FeatureFlag<string, string> | null>;
    /**
     * Any overrides to apply to the feature flags
     * These overrides are applied on top of feature flags received from Hirer Graph.
     */
    overrides?: Array<DistributedOmit<F, 'reason'>>;
    children: ReactNode;
  }>;
  /**
   * Feature keys sent to Hirer Graph
   */
  featureKeys: Array<F['key']>;
}

export function createFeatureFlagsContext<
  F extends FeatureFlag<K, V>,
  K extends string = F['key'],
  V extends string = F['variant'],
>(featureKeys: Array<F['key']>): FeatureFlagsContext<F, K> {
  const FeatureContext = createContext<Record<K, F> | null>(null);

  return {
    useFeatureFlag: (key) => {
      const contextValue = useContext(FeatureContext);
      if (!contextValue) {
        throw new Error(
          'useFeatureFlag must be used within a FeatureFlagsProvider',
        );
      }

      const feature = contextValue[key];
      if (!feature) {
        return {
          key,
          reason: 'LOCAL_FALLBACK',
          variant: 'control',
        } as Extract<F, { key: typeof key }>; // Note: This cast is safe, because we are adding the same key as a local fallback.
      }

      return feature as Extract<F, { key: typeof key }>; // Note: This cast is safe, because the key is one of the provided feature flags.
    },
    FeatureFlagsProvider: ({ children, featureFlags, overrides = [] }) => {
      const value: Record<string, F> = {};
      featureFlags.forEach((flag) => {
        if (flag) {
          value[flag.key] = flag as F; // Note: This cast is safe, because we are adding the feature flag as is, and the flag shape is set correctly when passed to the provider
        }
      });

      overrides.forEach((flag) => {
        value[flag.key] = {
          key: flag.key,
          reason: 'LOCAL_OVERRIDE',
          variant: flag.variant,
        } as F; // Note: This cast is safe, because we are adding the feature flag as is, and the flag shape is set correctly when passed to the provider.
      });

      return (
        <FeatureContext.Provider value={value}>
          {children}
        </FeatureContext.Provider>
      );
    },
    featureKeys,
  };
}
